import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
    Paper, Button, TextField, Box, Typography,
    CircularProgress, Alert, Snackbar, Stack, alpha, useTheme,
    Tooltip, AlertTitle, Chip,
} from '@mui/material';
import {
    Search as SearchIcon,
    PrintOutlined as PrintIcon,
    ErrorOutline as ErrorIcon,
    InfoOutlined as InfoIcon,
    HelpOutline as LegendIcon,
    DescriptionOutlined as ReportIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import ExportExcel from './ExportExcel';
import { useReactToPrint } from 'react-to-print';

// --- Helper Functions ---
const getAttendanceColor = (status, theme) => {
    const s = status ? status.toUpperCase() : '';
    switch (s) {
        case 'P': return alpha(theme.palette.success.light, 0.3);
        case 'A': return alpha(theme.palette.error.light, 0.3);
        case 'L': return alpha(theme.palette.warning.light, 0.4);
        case 'H': return alpha(theme.palette.info.light, 0.3);
        case 'NW': return alpha(theme.palette.grey[200], 0.8);
        default: return 'transparent';
    }
};

const getAttendanceTooltip = (status) => {
    const s = status ? status.toUpperCase() : '';
    switch (s) {
        case 'P': return 'Present';
        case 'A': return 'Absent';
        case 'L': return 'Leave';
        case 'H': return 'Holiday';
        case 'NW': return 'Non-Working';
        default: return 'Unknown';
    }
};

const getSummaryLabel = (shortLabel) => {
    switch (shortLabel) {
        case 'P': return 'Present';
        case 'A': return 'Absent';
        case 'H': return 'Holidays';
        case 'L': return 'Leave';
        case 'Work': return 'Work Days';
        case 'Non-Work': return 'Non-Work';
        default: return shortLabel;
    }
};

const generateDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dateArray = [];
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return dateArray;
    let currentDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const finalEndDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (currentDate <= finalEndDate) {
        dateArray.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateArray;
};

const getDateKey = (dateObj) => {
    if (!dateObj || isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatHeaderDate = (dateObj) => {
    if (!dateObj || isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// --- Printable Component ---
const EmployeeAttendancePrintableTable = React.forwardRef(({
    attendanceDetails,
    allDatesInRange,
    schoolName,
    fromDate,
    toDate,
    summaryColumns
}, ref) => {
    const theme = useTheme();
    if (!attendanceDetails || attendanceDetails.length === 0) return null;

    return (
        <div ref={ref} style={{ width: '100%', margin: 'auto' }}>
            <style type="text/css" media="print">{`
                @page {
                    size: A3 landscape;
                    margin: 8mm;
                }
                body {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    margin: 0;
                    padding: 0;
                    font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif;
                    font-size: 7pt;
                }
                table {
                    border-collapse: collapse;
                    width: 100%;
                    table-layout: auto;
                    border: 1px solid #ccc;
                }
                th, td {
                    border: 1px solid #ccc;
                    padding: 2px 3px;
                    text-align: center;
                    word-wrap: break-word;
                    vertical-align: middle;
                }
                th {
                    background-color: #e8e8e8 !important;
                    font-weight: 600;
                    font-size: 7.5pt;
                }
                .print-header {
                    text-align: center;
                    margin-bottom: 8px;
                    font-size: 9pt;
                }
                .print-header h4, .print-header p {
                    margin: 1px 0;
                }
                .text-left {
                    text-align: left !important;
                }
                td {
                    background-color: inherit !important;
                }
                .no-print { display: none !important; }
            `}</style>
            <Box className="print-header">
                <Typography variant="h6" component="h4" sx={{ fontSize: '11pt', fontWeight: 600 }}>
                    {schoolName || 'Employee Attendance Report'}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '8pt' }}>
                    Period: {fromDate} to {toDate}
                </Typography>
            </Box>
            <Table size="small" aria-label="printable employee attendance table">
                <TableHead>
                    <TableRow>
                        <TableCell className="text-left" sx={{ minWidth: 90 }}>Employee</TableCell>
                        {summaryColumns.map(col => (
                            <TableCell key={`print-th-sum-${col.key}`} sx={{ minWidth: 35 }}>
                                {getSummaryLabel(col.label)}
                            </TableCell>
                        ))}
                        {allDatesInRange.map((dateObj) => (
                            <TableCell key={`print-th-day-${getDateKey(dateObj)}`} sx={{ minWidth: 35, writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                                {formatHeaderDate(dateObj)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {attendanceDetails.map((detail) => (
                        <TableRow key={`print-tr-${detail.employeeId}`}>
                            <TableCell className="text-left">{detail.employeeName?.trim() || `ID: ${detail.employeeId}`}</TableCell>
                            {summaryColumns.map(col => (
                                <TableCell key={`print-td-sum-${col.key}-${detail.employeeId}`}>
                                    {detail[col.key] ?? 0}
                                </TableCell>
                            ))}
                            {allDatesInRange.map((dateObj) => {
                                const dateKey = getDateKey(dateObj);
                                const dayStatus = detail.dailyAttendance?.[dateKey] || 'A';
                                const color = getAttendanceColor(dayStatus, theme);
                                return (
                                    <TableCell
                                        key={`print-td-day-${dateKey}-${detail.employeeId}`}
                                        style={{ backgroundColor: color }}
                                    >
                                        {dayStatus}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
});

// --- Main Component ---
const EmployeeAttendance = ({ iSchoolId }) => {
    const theme = useTheme();
    const printableComponentRef = useRef();

    const [schoolName, setSchoolName] = useState('');
    const [attendanceDetails, setAttendanceDetails] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [allDatesInRange, setAllDatesInRange] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    const [isFetched, setIsFetched] = useState(false);

    const summaryColumns = [
        { key: 'totalPresent', label: 'P' },
        { key: 'totalAbsent', label: 'A' },
        { key: 'totalHolidays', label: 'H' },
        { key: 'totalLeave', label: 'L' },
        { key: 'totalWorkingDays', label: 'Work' },
        { key: 'totalNonWorkingDays', label: 'Non-Work' },
    ];

    const attendanceLegends = [
        { status: 'P', label: 'Present', color: getAttendanceColor('P', theme) },
        { status: 'A', label: 'Absent', color: getAttendanceColor('A', theme) },
        { status: 'L', label: 'Leave', color: getAttendanceColor('L', theme) },
        { status: 'H', label: 'Holiday', color: getAttendanceColor('H', theme) },
        { status: 'NW', label: 'Non-Working', color: getAttendanceColor('NW', theme) },
    ];

    const showNotification = useCallback((message, severity = 'info') => {
        setNotification({ open: true, message, severity });
    }, []);

    useEffect(() => {
        const fetchSchoolName = async () => {
            if (!iSchoolId) { setSchoolName('School ID Missing'); return; }
            try {
                const { data } = await axiosInstance.get(`/api/school-settings/school/${iSchoolId}`, { headers: { schoolId: iSchoolId } });
                setSchoolName(data?.schoolName || 'School Name Unavailable');
            } catch (err) { setSchoolName('School Name Unavailable'); }
        };
        fetchSchoolName();
    }, [iSchoolId]);

    const handleCloseNotification = useCallback((event, reason) => {
        if (reason === 'clickaway') return;
        setNotification(prev => ({ ...prev, open: false }));
    }, []);

    const fetchAttendanceDetails = useCallback(async () => {
        setError(null);
        setAttendanceDetails([]); setAllDatesInRange([]);

        if (!fromDate || !toDate) { showNotification('Please select From and To Dates.', 'warning'); return; }
        const startDate = new Date(fromDate); const endDate = new Date(toDate);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) { showNotification('Invalid date format.', 'error'); return; }
        if (startDate > endDate) { showNotification('From Date cannot be after To Date.', 'warning'); return; }

        const diffTime = Math.abs(endDate - startDate); const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const maxDays = 90;
        if (diffDays > maxDays) { showNotification(`Date range cannot exceed ${maxDays} days. Please select a shorter period.`, 'warning'); return; }

        setIsFetched(true);
        setIsLoading(true);

        try {
            const response = await axiosInstance.get('/api/employee-attendance/details', {
                params: { startDate: fromDate, endDate: toDate }, headers: { schoolId: iSchoolId },
            });
            const data = Array.isArray(response.data) ? response.data : [];
            setAttendanceDetails(data);
            const dateArray = generateDateRange(fromDate, toDate);
            setAllDatesInRange(dateArray);
            if (data.length === 0) showNotification('No attendance records found for the selected period.', 'info');
        } catch (err) {
            console.error("Fetch Error:", err);
            const errorMessage = getApiErrorMessage(err, 'Failed to fetch attendance data.');
            setError({ message: errorMessage });
            showNotification(errorMessage, 'error');
            setAttendanceDetails([]); setAllDatesInRange([]);
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate, iSchoolId, showNotification]);

    const handlePrint = useReactToPrint({
        content: () => printableComponentRef.current,
        documentTitle: `Employee_Attendance_${schoolName.replace(/ /g, '_')}_${fromDate}_to_${toDate}`,
        pageStyle: `@page { size: A3 landscape; margin: 8mm; }`,
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                setIsLoading(true);
                setTimeout(resolve, 250);
            });
        },
        onAfterPrint: () => setIsLoading(false),
        removeAfterPrint: true,
    });

    const isDataAvailable = !isLoading && !error && attendanceDetails.length > 0;
    const showNoDataMessage = isFetched && !isLoading && !error && attendanceDetails.length === 0;
    const showInitialMessage = !isFetched && !isLoading && !error;

    const headerCellStyle = {
        py: 0.8,
        px: 1,
        bgcolor: '#F5F7FB',
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontWeight: 600,
        fontSize: '0.78rem',
        whiteSpace: 'nowrap',
        textAlign: 'center',
    };

    const bodyCellStyle = {
        py: 0.6,
        px: 1,
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        textAlign: 'center',
    };

    const stickyColumnStyle = {
        position: 'sticky',
        left: 0,
        zIndex: theme.zIndex.appBar - 1,
        bgcolor: alpha(theme.palette.background.paper, 0.98),
        boxShadow: `2px 0 4px -2px ${alpha(theme.palette.common.black, 0.1)}`,
        textAlign: 'left',
    };

    return (
        <Stack spacing={1.2} sx={{ minHeight: '100%', p: 0, bgcolor: 'transparent' }}>

            <Paper variant='outlined' sx={{ p: 1.25, borderRadius: 1.5, boxShadow: 'none' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap>
                        <TextField
                            label="From Date" type="date" size="small"
                            value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: toDate || new Date().toISOString().split('T')[0] }}
                            sx={{ minWidth: 140 }}
                        />
                        <TextField
                            label="To Date" type="date" size="small"
                            value={toDate} onChange={(e) => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: fromDate || undefined, max: new Date().toISOString().split('T')[0] }}
                            sx={{ minWidth: 140 }}
                            disabled={!fromDate}
                        />
                        <Button
                            variant="contained" size="medium"
                            onClick={fetchAttendanceDetails}
                            disabled={isLoading || !fromDate || !toDate}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                            sx={{ height: 40 }}
                        >
                            Fetch Report
                        </Button>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
                        {isDataAvailable && (
                            <ExportExcel
                                attendanceDetails={attendanceDetails}
                                schoolName={schoolName}
                                fromDate={fromDate}
                                toDate={toDate}
                                isStudent={false}
                                allDatesInRange={allDatesInRange}
                                summaryColumns={summaryColumns}
                            />
                        )}
                        <Tooltip title="Print Report">
                            <span>
                                <Button
                                    variant="outlined" size="medium" color="secondary"
                                    onClick={handlePrint}
                                    disabled={!isDataAvailable || isLoading}
                                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                                    sx={{ height: 40 }}
                                >
                                    Print
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            <Paper variant='outlined' sx={{ p: 0.9, borderRadius: 1.5, boxShadow: 'none' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mr: 1 }}>
                        <LegendIcon fontSize="small"/> Legend:
                    </Typography>
                    {attendanceLegends.map(legend => (
                        <Chip
                            key={legend.status}
                            label={legend.label}
                            size="small"
                            sx={{
                                bgcolor: legend.color,
                                color: theme.palette.getContrastText(legend.color),
                                border: `1px solid ${alpha(theme.palette.getContrastText(legend.color), 0.3)}`,
                                height: 24,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                            }}
                        />
                    ))}
                </Stack>
            </Paper>

            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, flexGrow: 1, flexDirection: 'column', gap: 1 }}>
                        <CircularProgress />
                        <Typography color="text.secondary" variant="body1" sx={{ mt: 1 }}>Loading Attendance Data...</Typography>
                        <Typography color="text.secondary" variant="body2">Please wait.</Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, flexGrow: 1 }}>
                        <Alert severity="error" icon={<ErrorIcon />} sx={{ width: '100%', maxWidth: '600px', alignItems: 'center' }}>
                            <AlertTitle sx={{ fontWeight: 600 }}>Error Fetching Data</AlertTitle>
                            {error.message || 'An unknown error occurred.'}
                        </Alert>
                    </Box>
                ) : showNoDataMessage ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, flexGrow: 1, flexDirection: 'column', gap: 1, textAlign: 'center' }}>
                        <InfoIcon color="action" sx={{ fontSize: 40, mb: 1 }}/>
                        <Typography variant="h6" color="text.secondary">No Records Found</Typography>
                        <Typography variant="body2" color="text.secondary">There is no employee attendance data matching your selected criteria.</Typography>
                    </Box>
                ) : isDataAvailable ? (
                    <Paper variant="outlined" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 1.5, boxShadow: 'none' }}>
                        <TableContainer sx={{
                            flexGrow: 1,
                            overflow: 'auto',
                            '&::-webkit-scrollbar': { height: 8, width: 8 },
                            '&::-webkit-scrollbar-track': { bgcolor: '#f1f1f1' },
                            '&::-webkit-scrollbar-thumb': { bgcolor: theme.palette.mode === 'light' ? '#c1c1c1' : '#555', borderRadius: 4 },
                            '&::-webkit-scrollbar-thumb:hover': { bgcolor: theme.palette.mode === 'light' ? '#a8a8a8' : '#777' }
                        }}>
                            <Table size="small" stickyHeader aria-label="employee attendance table">
                                <TableHead>
                                    <TableRow sx={{ '& th': headerCellStyle }}>
                                        <TableCell sx={{ ...headerCellStyle, ...stickyColumnStyle, minWidth: 180 }}>
                                            Employee
                                        </TableCell>
                                        {summaryColumns.map(col => (
                                            <TableCell key={`th-sum-${col.key}`} sx={{ minWidth: 50 }}>
                                                <Tooltip title={getSummaryLabel(col.label)} placement="top">
                                                    <span>{col.label}</span>
                                                </Tooltip>
                                            </TableCell>
                                        ))}
                                        {allDatesInRange.map((dateObj) => (
                                            <TableCell key={`th-day-${getDateKey(dateObj)}`} sx={{ minWidth: 55 }}>
                                                {formatHeaderDate(dateObj)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {attendanceDetails.map((detail) => {
                                        const { employeeId, employeeName, dailyAttendance = {} } = detail;
                                        return (
                                            <TableRow
                                                key={employeeId}
                                                hover
                                                sx={{ '& td': { ...bodyCellStyle, borderBottom: `1px solid ${theme.palette.divider}` }, '&:last-child td': { borderBottom: 0 } }}
                                            >
                                                <TableCell sx={{ ...bodyCellStyle, ...stickyColumnStyle, fontWeight: 500 }}>
                                                    {employeeName?.trim() || `ID: ${employeeId}`}
                                                </TableCell>
                                                {summaryColumns.map(col => (
                                                    <TableCell key={`td-sum-${col.key}-${employeeId}`} sx={{ fontWeight: 500 }}>
                                                        {detail[col.key] ?? 0}
                                                    </TableCell>
                                                ))}
                                                {allDatesInRange.map((dateObj) => {
                                                    const dateKey = getDateKey(dateObj);
                                                    const dayStatus = dailyAttendance[dateKey] || 'A';
                                                    const color = getAttendanceColor(dayStatus, theme);
                                                    const tooltip = getAttendanceTooltip(dayStatus);
                                                    return (
                                                        <TableCell
                                                            key={`td-day-${dateKey}-${employeeId}`}
                                                            sx={{ bgcolor: color, fontWeight: 500, cursor: 'default' }}
                                                        >
                                                            <Tooltip title={tooltip} placement="top">
                                                                <span>{dayStatus}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ) : showInitialMessage ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, flexGrow: 1, flexDirection: 'column', gap: 1, textAlign: 'center', border: `1px dashed ${theme.palette.divider}`, borderRadius: 2, bgcolor: alpha(theme.palette.primary.light, 0.05) }}>
                        <ReportIcon color="action" sx={{ fontSize: 40, mb: 1 }}/>
                        <Typography variant="h6" color="text.primary">Select Dates to Generate Report</Typography>
                        <Typography variant="body2" color="text.secondary">Choose the 'From Date' and 'To Date' above and click "Fetch Report" to view employee attendance.</Typography>
                    </Box>
                ) : null}
            </Box>

            <div style={{ display: 'none', visibility: 'hidden', height: 0, overflow: 'hidden' }}>
                <EmployeeAttendancePrintableTable
                    ref={printableComponentRef}
                    attendanceDetails={attendanceDetails}
                    allDatesInRange={allDatesInRange}
                    schoolName={schoolName}
                    fromDate={fromDate}
                    toDate={toDate}
                    summaryColumns={summaryColumns}
                />
            </div>

            <Snackbar open={notification.open} autoHideDuration={4000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={notification.severity} variant="filled" elevation={6} sx={{ width: '100%', maxWidth: 500 }} onClose={handleCloseNotification}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

export default EmployeeAttendance;
